import {
  ComplaintById,
  ComplaintStatus,
} from "@modules/plaintes/plaintes.model.ts";
import { TabPanel, TabView } from "primereact/tabview";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useAppSelector } from "@core/store/hook.ts";
import { loadingEditComplaint } from "@core/slice/ComplaintSlice.ts";
import { FetchStatusType } from "@core/modeles/model.ts";

interface DetailHistoryProps {
  complaint: ComplaintById;
  status: ComplaintStatus;
  isMeasuresToBeTaken: boolean;
  setVisibleConfirmDialog: (a: boolean) => void;
  isDateOlderThan7Days: (a: any) => number;
}

const DetailHistory: React.FC<DetailHistoryProps> = ({
  complaint,
  status,
  isMeasuresToBeTaken,
  setVisibleConfirmDialog,
  isDateOlderThan7Days,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const loading = useAppSelector(loadingEditComplaint);

  const isDisabled = false;

  function handlePrevious() {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("type") === "external") {
      navigate("/");
    } else {
      navigate("/complaint/list-complaint");
    }
  }

  const transferStatus = (status: string) => {
    return (
      <>
        {status === ComplaintStatus.PENDING && (
          <div className="flex items-center border border-solid border-[#fcad12] rounded-3xl p-1.5 w-max">
            <i className="pi pi-spinner p-1 bg-[#fcad12] rounded-full text-white text-[10px]" />
            <span className="ml-2 text-[#fcad12] text-[10px] font-medium">
              En attente
            </span>
          </div>
        )}
        {status === ComplaintStatus.ESCALATED && (
          <div className="flex items-center border border-solid border-[#d8d777] rounded-3xl p-1.5 w-max">
            <i className="pi pi-undo p-1 bg-[#8B4513] rounded-full text-white text-[10px]" />
            <span className="ml-2 text-[#8B4513] text-[10px] font-medium">
              Escaladé
            </span>
          </div>
        )}
        {status === ComplaintStatus.IN_PROGRESS && (
          <div className="flex items-center border border-solid border-[#fffc33] rounded-3xl p-1.5 w-max">
            <i className="pi pi-undo p-1 bg-[#fffc33] rounded-full text-white text-[10px]" />
            <span className="ml-2 text-[#FFC107] text-[10px] font-medium">
              En cours
            </span>
          </div>
        )}
        {status === ComplaintStatus.RESOLVED_POSITIVE && (
          <div className="flex items-center border border-solid border-[#4CAF50] rounded-3xl p-1.5 w-max">
            <i className="pi pi-check p-1 bg-[#4CAF50] rounded-full text-white text-[10px]" />
            <span className="ml-2 text-[#4CAF50] text-[10px] font-medium">
              Résolu positif
            </span>
          </div>
        )}
        {status === ComplaintStatus.RESOLVED_NEGATIVE && (
          <div className="flex items-center border border-solid border-[#4CAF50] rounded-3xl p-1.5 w-max">
            <i className="pi pi-check p-1 bg-[#f90e0e] rounded-full text-white text-[10px]" />
            <span className="ml-2 text-[#4CAF50] text-[10px] font-medium">
              Résolu négatif
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="mx-8">
      <TabView>
        <TabPanel header="DETAIL">
          <div className="bg-gray-200 p-1 sm:p-4 rounded-[20px] grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-[10px] p-2 bg-white flex flex-col">
              <span className="text-black text-sm font-semibold mb-2">
                Détail de la plainte :
              </span>
              <span className="text-[#6C757D] text-sm font-semibold break-words">
                {complaint.description}
              </span>
            </div>

            <div className="rounded-[10px] p-2 bg-white flex flex-col">
              <span className="text-black text-sm font-semibold mb-2">
                Décision finale :
              </span>
              <span className="text-[#6C757D] text-sm font-semibold break-words">
                {complaint.actionTaken}
              </span>
            </div>
            <div className="rounded-[10px] p-2 bg-white flex flex-col">
              <span className="text-black text-sm font-semibold mb-2">
                Individu ou institution désignés :
              </span>
              <span className="text-[#6C757D] text-sm font-semibold">
                Ministère de la population et des Solidarités
              </span>
            </div>
            <div className="rounded-[10px] p-2 bg-white flex flex-col">
              <span className="text-black text-sm font-semibold mb-2">
                Transfert :
              </span>
              <span className="text-[#6C757D] text-sm font-semibold">
                {complaint.status !== ComplaintStatus.ARCHIVED
                  ? complaint.complaintUsers[0].profil.name
                  : complaint.complaintUsers[0].user.firstName}
              </span>
            </div>
          </div>

          {isDateOlderThan7Days(complaint.createdAt) > 0 &&
            complaint.status === ComplaintStatus.RESOLVED_NEGATIVE && (
              <div className="w-full text-center text-red-400">
                <i>
                  Cette plainte sera <strong>archivable</strong> dans{" "}
                  {isDateOlderThan7Days(complaint.createdAt)}{" "}
                  {isDateOlderThan7Days(complaint.createdAt) > 1
                    ? "jours"
                    : "jour"}
                  .
                </i>
              </div>
            )}
          {isMeasuresToBeTaken && complaint.report === null && (
            <div className="flex gap-3 justify-content-end mt-7">
              <div className="flex justify-content-end">
                <Button
                  className={classNames("btn bg-[#fcad12] w-[175px]", {
                    "btn-secondary-disabled":
                      isDisabled ||
                      (isDateOlderThan7Days(complaint.createdAt) > 0 &&
                        complaint.status === ComplaintStatus.RESOLVED_NEGATIVE),
                  })}
                  onClick={() => setVisibleConfirmDialog(true)}
                  disabled={
                    isDisabled ||
                    (isDateOlderThan7Days(complaint.createdAt) > 0 &&
                      complaint.status === ComplaintStatus.RESOLVED_NEGATIVE)
                  }
                >
                  {loading === FetchStatusType.LOADING && (
                    <div className="mr-2 w-4 h-4 rounded-full animate-spin border-y-4 border-solid border-white border-t-transparent shadow-md pe-2"></div>
                  )}
                  <i
                    className="fas fa-archive mr-2"
                    style={{ color: "black" }}
                  />
                  Archiver la plainte
                </Button>
              </div>
            </div>
          )}
          {status === ComplaintStatus.ARCHIVED && (
            <div className="w-full text-center text-red-400">
              <i>
                Cette plainte est archivée. Les actions de transfert sont
                désactivées.
              </i>
            </div>
          )}
          {(!isMeasuresToBeTaken ||
            (isMeasuresToBeTaken && complaint.report !== null)) && (
            <div className="flex justify-end mt-12">
              <Button
                className="justify-center w-[120px] bg-[#64748B] text-white font-bold py-2 px-4 rounded-md gap-2"
                onClick={handlePrevious}
              >
                Retour
              </Button>
            </div>
          )}
        </TabPanel>

        {/* Historique */}
        <TabPanel header="HISTORIQUE">
          {complaint.complaintUsers.map((e, index) => {
            return (
              <React.Fragment key={e.id}>
                {e.fieldChanged === "TRANSFER" && (
                  <div className="border border-solid border-[#E5E5E5] rounded p-2 mb-3">
                    <span className="text-black text-sm font-semibold">
                      Transféré vers {e.profil.name}
                    </span>
                    <div className="flex items-center mt-1">
                      <div className="text-[#6C757D] flex items-center">
                        <i className="pi pi-calendar text-xs" />
                        <span className="ml-2 text-xs">
                          {new Date(e.createdAt).toISOString().split("T")[0]}
                        </span>
                      </div>
                      <div className="text-[#6C757D] flex items-center ml-3">
                        <i className="pi pi-user text-xs" />
                        <span className="ml-2 text-xs">
                          {e.user.firstName} {e.user.lastName}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="ml-2 text-xs">
                        {e.supportTypes
                          .map((supportType) => supportType.description)
                          .join(", ")}
                      </span>
                    </div>
                    <hr className="mt-3" />
                    <span className="text-xs break-words">{e.actionTaken}</span>
                  </div>
                )}

                {e.fieldChanged === "STATUS" && (
                  <div className="border border-solid border-[#E5E5E5] rounded p-2 mb-3">
                    <span className="text-black text-sm font-semibold">
                      Changement de statut
                    </span>
                    <div className="flex items-center mt-1">
                      <div className="text-[#6C757D] flex items-center">
                        <i className="pi pi-calendar text-xs" />
                        <span className="ml-2 text-xs">
                          {new Date(e.createdAt).toISOString().split("T")[0]}
                        </span>
                      </div>
                      <div className="text-[#6C757D] flex items-center ml-3">
                        <i className="pi pi-user text-xs" />
                        <span className="ml-2 text-xs">
                          {e.user.firstName} {e.user.lastName}
                        </span>
                      </div>
                    </div>
                    <hr className="mt-3" />

                    <div className="flex items-center mt-2.5">
                      {transferStatus(e.oldStatus)}

                      {complaint.complaintUsers.length > 1 &&
                        index !== complaint.complaintUsers.length - 1 && (
                          <i className="pi pi-arrow-right-arrow-left p-1 rounded-full text-[#6C757D] text-base mx-1" />
                        )}

                      {transferStatus(e.newStatus)}
                    </div>
                  </div>
                )}
                {e.fieldChanged === "REF" && (
                  <div className="border border-solid border-[#E5E5E5] rounded p-2 mb-3">
                    <span className="text-black text-sm font-semibold">
                      Changement de réference
                    </span>
                    <div className="flex items-center mt-1">
                      <div className="text-[#6C757D] flex items-center">
                        <i className="pi pi-calendar text-xs" />
                        <span className="ml-2 text-xs">
                          {new Date(e.createdAt).toISOString().split("T")[0]}
                        </span>
                      </div>
                      <div className="text-[#6C757D] flex items-center ml-3">
                        <i className="pi pi-user text-xs" />
                        <span className="ml-2 text-xs">
                          {e.user.firstName} {e.user.lastName}
                        </span>
                      </div>
                    </div>
                    <hr className="mt-3" />

                    <div className="flex items-center mt-2.5">
                      <div className="flex items-center p-1.5 w-max">
                        <span className="ml-2 font-medium">{e.oldRef}</span>
                      </div>

                      {complaint.complaintUsers.length > 1 &&
                        index !== complaint.complaintUsers.length - 1 && (
                          <i className="pi pi-arrow-right-arrow-left p-1 rounded-full text-[#6C757D] text-base mx-1" />
                        )}

                      <div className="flex items-center p-1.5 w-max">
                        <span className="ml-2 font-medium">{e.newRef}</span>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          <div className="flex justify-end mt-12">
            <Button
              className="justify-center w-[120px] bg-[#64748B] text-white font-bold py-2 px-4 rounded-md gap-2"
              onClick={handlePrevious}
            >
              Retour
            </Button>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default DetailHistory;
